import React, { useState, useEffect } from 'react';
import { Box, Button, TextField, Typography, Paper, FormControl, InputLabel, Select, MenuItem, Alert, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { apiRequest, apiGet } from '../../../utils/fetch';
import { UsersPurchasePlansInterface, PurchasePlanInterface, GenericResponseInterface } from '../../../interfaces/interfaces';

const initialPlan: Partial<UsersPurchasePlansInterface> = {
  userId: 0,
  purchasePlanId: 0,
  startDate: '',
  endDate: '',
  active: true,
};

const UsersPurchasePlansAdminAddPurchasePlanPage: React.FC = () => {
  const [plan, setPlan] = useState<Partial<UsersPurchasePlansInterface>>(initialPlan);
  const [purchasePlans, setPurchasePlans] = useState<PurchasePlanInterface[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    apiGet<GenericResponseInterface<PurchasePlanInterface[]>>('/purchasePlans')
      .then(result => {
        if (result.status && result.data) {
          setPurchasePlans(result.data);
        }
      })
      .catch(() => setPurchasePlans([]));
  }, []);

  const handleChange = (field: keyof UsersPurchasePlansInterface, value: any) => {
    setPlan(a => ({ ...a, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    try {
      const result = await apiRequest<GenericResponseInterface<UsersPurchasePlansInterface>>('/usersPurchasePlans/insert', 'POST', plan);
      if (result.status) {
        setSuccess(result.message || 'User purchase plan created successfully!');
        setDialogOpen(true);
        setPlan(initialPlan);
      } else {
        setError(result.message || 'Failed to create user purchase plan');
        setDialogOpen(true);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to create user purchase plan');
      setDialogOpen(true);
    }
  };

  return (
    <Paper sx={{ p: 3, maxWidth: 500, mx: 'auto', mt: 4 }}>
      <Typography variant="h5" gutterBottom>Add User Purchase Plan</Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
      <form onSubmit={handleSubmit}>
        <TextField
          label="User ID"
          type="number"
          value={plan.userId ?? ''}
          onChange={e => handleChange('userId', Number(e.target.value))}
          fullWidth
          margin="dense"
          required
        />
        <FormControl fullWidth margin="dense" required>
          <InputLabel id="purchase-plan-label">Purchase Plan</InputLabel>
          <Select
            labelId="purchase-plan-label"
            value={plan.purchasePlanId ?? ''}
            label="Purchase Plan"
            onChange={e => handleChange('purchasePlanId', Number(e.target.value))}
          >
            {purchasePlans.map(pp => (
              <MenuItem key={pp.id} value={pp.id}>
                {pp.planName ? `${pp.planName} (ID: ${pp.id})` : `ID: ${pp.id}`}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          label="Start Date"
          type="date"
          value={plan.startDate ?? ''}
          onChange={e => handleChange('startDate', e.target.value)}
          fullWidth
          margin="dense"
          InputLabelProps={{ shrink: true }}
          required
        />
        <TextField
          label="End Date"
          type="date"
          value={plan.endDate ?? ''}
          onChange={e => handleChange('endDate', e.target.value)}
          fullWidth
          margin="dense"
          InputLabelProps={{ shrink: true }}
          required
        />
        <FormControl fullWidth margin="dense">
          <InputLabel id="active-label">Active</InputLabel>
          <Select
            labelId="active-label"
            value={plan.active !== undefined ? (plan.active ? 'yes' : 'no') : 'yes'}
            label="Active"
            onChange={e => handleChange('active', e.target.value === 'yes')}
          >
            <MenuItem value="yes">Yes</MenuItem>
            <MenuItem value="no">No</MenuItem>
          </Select>
        </FormControl>
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <Button type="submit" variant="contained" color="primary">
            Create User Purchase Plan
          </Button>
        </Box>
      </form>
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
        <DialogTitle>{success ? 'Success' : 'Error'}</DialogTitle>
        <DialogContent>
          <Typography>{success || error}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>OK</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default UsersPurchasePlansAdminAddPurchasePlanPage;